import { useCallback, useMemo } from "react";
import type { Editor } from "grapesjs";
import { usePatchDraftMutation } from "../../../workspace/hooks/useDraftPatchMutation";
import { fileToDataUrl } from "../../project/helpers/file-to-data-url.helpers";

const actionButton: React.CSSProperties = {
    padding: "10px 20px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    transition: "all 0.2s",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
};

const saveButtonStyle: React.CSSProperties = {
    background: "var(--fx-surface-base, #FFFFFF)",
    color: "var(--fx-surface-textsecondary, #64748B)",
    border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
};

const disabledStyle: React.CSSProperties = {
    opacity: 0.6,
    cursor: "not-allowed",
};

type Props = {
    draftId: string;
    favicon: File | undefined;
    editor: Editor | null;
    projectName?: string;
    onSaved?: (updated: any) => void;
    onConflict?: () => void;
};

export function SaveDraftButton({
    draftId,
    favicon,
    editor,
    projectName,
    onSaved,
    onConflict,
}: Props) {
    const patchMutation = usePatchDraftMutation();



    const disabled = useMemo(() => {
        return !draftId || !editor || patchMutation.isPending;
    }, [draftId, editor, patchMutation.isPending]);

    const handleSave = useCallback(async () => {
        if (!editor) return;

        const html = editor.getHtml() || "";
        const css = editor.getCss() || "";

        try {
            const updated = await patchMutation.mutateAsync({
                draftId,
                payload: {
                    projectName,
                    favicon,
                    content: { html, css },
                    documentHtml: html,
                },
            });

            onSaved?.(updated);
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 409) {
                onConflict?.();
                return;
            }
            throw e;
        }
    }, [editor, patchMutation, draftId, projectName, onSaved, onConflict]);

    return (
        <button
            onClick={handleSave}
            disabled={disabled}
            style={{
                ...actionButton,
                ...saveButtonStyle,
                ...(disabled ? disabledStyle : null),
            }}
        >
            {patchMutation.isPending ? "Saving..." : "Save"}
        </button>
    );
}
